import { IconAdjustmentsHorizontal } from "@tabler/icons-react";
import clsx from "clsx";

import { FEATURE_FLAGS, FILTER_CATEGORIES, language } from "@chef/constants";
import { Chip, ScrollableWrapper } from "@chef/components";
import { useFlag } from "@chef/feature-flags";
import { isEqualStrings } from "@chef/utils/equal";
import { isEmptyArray } from "@chef/utils/array";
import { useAppDispatch } from "@chef/state-management/hooks";
import { openModal } from "@chef/state-management";

import { FilterChips } from "./FilterChips";

import { intl } from "./FilterNavigation.Intl";

interface FilterNavigationProps {
  items: {
    value: string;
    label: string;
    count: number;
    type: "default" | "highlighted" | "secondary";
    isAll: boolean;
  }[];
  value: string;
  onChange: (newValue: string) => void;
  resetFilters?: () => void;

  showGradients?: boolean;
  leftClassName?: string;
  rightClassName?: string;
  className?: string;
}

export const FilterNavigation = ({
  items = [],
  value,
  onChange = () => null,
  showGradients = true,
  leftClassName = "bg-gradient-to-r from-white via-white",
  rightClassName = "bg-gradient-to-l from-white via-white",
  className,
  resetFilters,
}: FilterNavigationProps) => {
  const dispatch = useAppDispatch();

  const { enabled: abtest } = useFlag(FEATURE_FLAGS.FILTER_NAVIGATION);

  if (isEmptyArray(items)) {
    return (
      <div className="flex space-x-2">
        {[...Array(3)].map((_, index) => (
          <Chip className="w-20 animate-pulse" key={index} />
        ))}
      </div>
    );
  }

  const vals = value
    .toString()
    .split(",")
    .filter((v) => v !== "");

  const showcasedItems = items
    .filter(
      (item) =>
        item.type === "highlighted" ||
        (isEqualStrings(item.value.toString(), value.toString()) &&
          !item.isAll),
    )
    .sort((a, _) => (a.type === "highlighted" ? 1 : -1));

  const onClickChip = () =>
    dispatch(
      openModal({
        id: "FILTER_NAVIGATION",
        items,
        onClick: onChange,
        values: vals,
      }),
    );

  return (
    <>
      <div className={className}>
        <ScrollableWrapper
          language={language}
          leftClassName={leftClassName}
          rightClassName={rightClassName}
          showGradients={showGradients}
        >
          {/* TODO: A/B test new mobile version */}
          <ul
            className={clsx(
              "flex relative gap-2 px-4 -mx-4 md:px-0 md:mx-0",
              abtest ? "lg:hidden" : "hidden",
            )}
            id="filter-navigate-mobile"
          >
            <li>
              <Chip className="tabular-nums" onClick={onClickChip}>
                <div className="flex items-center gap-1">
                  <IconAdjustmentsHorizontal className="w-4 h-4" />
                  <span>{intl.ALL}</span>
                </div>
              </Chip>
            </li>
            <FilterChips
              selectedClassName="!bg-informationBG !border-information"
              items={showcasedItems}
              handleClick={onChange}
              values={vals}
              highlight={false}
              chipIcon="cross"
              alwaysShowCount
            />
          </ul>
          {/* TODO: A/B test old and desktop version*/}
          <ul
            className={clsx(
              "relative flex gap-2 px-4 -mx-4 md:px-0 md:mx-0",
              abtest && "hidden lg:flex",
            )}
            id="filter-navigate-desktop"
          >
            <FilterChips items={items} handleClick={onChange} values={vals} />
          </ul>
        </ScrollableWrapper>
        {resetFilters && (
          <div className="flex sticky top-0 z-[15] bg-background -mx-4 px-4 py-2 mt-2">
            {value !== FILTER_CATEGORIES.ALL_CATEGORIES_SLUG && (
              <div className="flex justify-end w-full">
                <button
                  onClick={resetFilters}
                  className="text-sm underline text-primary"
                >
                  {intl.REMOVE_FILTER}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
